import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter } from "react-router";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { AppShell } from "@/components/Layout/AppShell";
import { PageSkeleton } from "@/components/Layout/PageSkeleton";

// Each route is its own chunk (loaded on navigation, not on initial load) -
// this is what keeps heavy per-page deps (mapbox-gl, recharts, jspdf,
// qr-code-styling) out of the main bundle.
const Dashboard = lazy(() => import("./containers/Dashboard"));
const Users = lazy(() => import("./containers/Users"));
const User = lazy(() => import("./containers/User"));
const Devices = lazy(() => import("./containers/Devices"));
const Device = lazy(() => import("./containers/Device"));
const DeviceBrand = lazy(() => import("./containers/DeviceBrand"));
const DeviceBrands = lazy(() => import("./containers/DeviceBrands"));
const Organisations = lazy(() => import("./containers/Organisations"));
const Organisation = lazy(() => import("./containers/Organisation"));
const LNSs = lazy(() => import("./containers/LNSs"));
const Settings = lazy(() => import("./containers/Settings"));

// Auth is gated once here at the shell level (covers every route, including
// /device/:id and /user/:id, which previously had no withAuthenticationRequired
// wrapper at all) rather than per-container.
const ProtectedAppShell = withAuthenticationRequired(AppShell);

function withSuspense(element: ReactNode) {
  return <Suspense fallback={<PageSkeleton />}>{element}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <ProtectedAppShell />,
    children: [
      { index: true, element: withSuspense(<Dashboard />) },
      { path: "user", element: withSuspense(<Users />) },
      { path: "user/:id", element: withSuspense(<User />) },
      { path: "device", element: withSuspense(<Devices />) },
      { path: "device/:id", element: withSuspense(<Device />) },
      { path: "device/brand", element: withSuspense(<DeviceBrands />) },
      { path: "device/brand/:id", element: withSuspense(<DeviceBrand />) },
      { path: "organisation", element: withSuspense(<Organisations />) },
      { path: "organisation/:id", element: withSuspense(<Organisation />) },
      { path: "lns", element: withSuspense(<LNSs />) },
      { path: "settings", element: withSuspense(<Settings />) },
    ],
  },
]);
